import ChipInput from 'components/ChipInput';
import * as React from 'react';
import { CONTAINER_NUMBERS_RE } from 'services/helpers/containers';

interface Props {
  /** Disables interaction and changes appearance to indicate */
  disabled?: boolean;
  /** Callback fired on mount and whenever the entered container numbers change */
  onContainersListed: (containerNumbers: string[]) => void;
  /** If an array of containers is provided, this will be set as the initial content on mount */
  prepopulatedContainers?: string[];
}

export const InputForm: React.FC<Props> = (props) => {
  const { onContainersListed, disabled, prepopulatedContainers = [] } = props;

  const [inputRef, setInputRef] = React.useState<HTMLDivElement | undefined>();
  const [chips, setChips] = React.useState<string[]>(prepopulatedContainers);
  const [isActive, setIsActive] = React.useState<boolean>(false);
  const [isMaximumReached, setIsMaximumReached] = React.useState<boolean>(false);
  const chipInputRef = React.useRef<ChipInput>(null);

  const handleOnChange = (values: string[], containerNumbers: string[]) => {
    setChips(values);
    onContainersListed(containerNumbers);
  };

  const clearInput = () => {
    if (chips.length > 0) {
      chipInputRef?.current?.updateChips([]);
      setIsMaximumReached(false);
    }
  };

  React.useEffect(() => {
    setIsActive(!disabled && chips.length > 0);
  }, [disabled, chips]);

  return (
    <div
      className={`grow basis-1 space-y-2 flex flex-col text-dark ${disabled && '!text-gray-600'}`}
    >
      <p className="font-medium text-dark">Add your list of container numbers here</p>
      <div className="space-y-1 w-full ">
        <div
          className={`h-48 rounded-md p-4 overflow-y-scroll ${!disabled && 'cursor-text'} border ${
            isActive ? 'border-blue-900' : 'border-gray-600'
          }`}
          onClick={() => inputRef?.focus()}
        >
          <ChipInput
            ref={chipInputRef}
            inputRef={(ref: HTMLDivElement) => setInputRef(ref)}
            classes={{
              inputContainer: 'w-full h-full flex flex-wrap content-start gap-y-2',
              inputRoot: 'w-48',
              input: 'p-0',
              copyButtonContainer: 'hidden',
              chip: {
                root: '',
                label: 'select-text',
              },
            }}
            InputProps={{
              'aria-label': 'add containers',
            }}
            defaultValue={prepopulatedContainers}
            fullWidth={false}
            disabled={disabled}
            onMaximumReached={() => setIsMaximumReached(true)}
            maxNumberChips={100}
            alwaysShowPlaceholder
            placeholder="Paste your containers here"
            transformChipLabel={(label: string) => label.toUpperCase()}
            transformChipValue={(value: string) => value.replace(/-/gi, '').toUpperCase()}
            removeCharacters={['"', "'", ' ', '“', '”']}
            checkIfChipIsValid={(value: string) => {
              return CONTAINER_NUMBERS_RE.test(value);
            }}
            onChange={handleOnChange}
            onDelete={() => isMaximumReached && setIsMaximumReached(false)}
            onFocus={() => setIsActive(true)}
            onBlur={() => !chips.length && setIsActive(false)}
          />
        </div>
        <div className="flex w-full">
          {isMaximumReached && (
            <h4 className="text-red-900">You can&apos;t add more than 100 containers at once.</h4>
          )}
          <h4
            onClick={clearInput}
            className={`underline ml-auto ${
              chips.length === 0 ? 'cursor-not-allowed text-gray-600' : 'cursor-pointer'
            }`}
          >
            Clear input
          </h4>
        </div>
      </div>
    </div>
  );
};
InputForm.displayName = 'AddContainers.InputForm';
